import { useState } from 'react';
import { rankData, rankTabs } from '@/mocks/home';
import SectionHeading from './SectionHeading';

const trendMeta = {
  up: { icon: 'ri-arrow-up-line', cls: 'text-primary-600' },
  flat: { icon: 'ri-subtract-line', cls: 'text-foreground-400' },
  down: { icon: 'ri-arrow-down-line', cls: 'text-secondary-600' },
};

const maxHeat = 1000;

export default function RankBoard() {
  const [tab, setTab] = useState<string>('总榜');
  const rows = rankData[tab] ?? rankData['总榜'];

  return (
    <section className="border-b border-background-200 bg-background-50">
      <div className="mx-auto max-w-[1400px] px-4 py-10 md:px-6 md:py-14">
        <SectionHeading
          tone="accent"
          eyebrow="热度榜单"
          title="黑料爆料热度榜 · 谁在持续升温"
          desc="按讨论量、转发量与关注曲线综合排序，每小时刷新一次，方便快速定位仍在发酵的事件。"
          action="查看完整榜单"
        />

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <div className="inline-flex rounded-full border border-background-300 bg-background-100 p-1">
              {rankTabs.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setTab(item)}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                    tab === item
                      ? 'bg-accent-500 text-foreground-950'
                      : 'text-foreground-600 hover:text-foreground-950'
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>

            <ul className="mt-4 overflow-hidden rounded-lg border border-background-300 bg-background-50">
              {rows.map((row, index) => {
                const trend = trendMeta[row.trend];
                const isTop = row.rank <= 3;
                return (
                  <li
                    key={`${tab}-${row.rank}`}
                    className="group flex items-center gap-4 border-b border-background-200 px-4 py-4 transition-colors last:border-b-0 hover:bg-background-100 md:px-5"
                  >
                    <span
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md font-heading text-sm font-black ${
                        isTop ? 'bg-primary-500 text-background-50' : 'bg-background-200 text-foreground-700'
                      }`}
                    >
                      {row.rank}
                    </span>

                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-bold text-foreground-950 group-hover:text-primary-700 md:text-base">
                        {row.title}
                      </p>
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
                        <span className="rounded bg-secondary-100 px-2 py-0.5 text-secondary-800">{row.angle}</span>
                        <span>{row.source}</span>
                      </div>
                    </div>

                    <div className="hidden w-40 shrink-0 xl:block">
                      <div className="h-1.5 w-full overflow-hidden rounded-full bg-background-200">
                        <div
                          className="h-full rounded-full bg-accent-500"
                          style={{ width: `${Math.min(100, (row.heat / maxHeat) * 100)}%` }}
                        />
                      </div>
                    </div>

                    <div className="w-20 shrink-0 text-right">
                      <p className="font-heading text-sm font-black text-foreground-900">{row.heat}万</p>
                      <p className={`flex items-center justify-end gap-0.5 text-xs ${trend.cls}`}>
                        <i className={`text-sm ${trend.icon}`} />
                        {row.delta}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          <aside className="lg:col-span-4">
            <div className="rounded-lg border border-background-300 bg-foreground-950 p-5 text-background-50">
              <div className="flex items-center justify-between">
                <h3 className="font-heading text-base font-bold">热度指纹</h3>
                <span className="flex items-center gap-1 text-xs text-accent-400">
                  <span className="h-1.5 w-1.5 animate-blip rounded-full bg-accent-400" />
                  实时
                </span>
              </div>
              <p className="mt-2 text-xs leading-relaxed text-background-300">
                近 12 小时讨论热度整体呈上行，第二次发酵节点出现在 23:05 前后。
              </p>

              <div className="mt-5 space-y-3">
                {[
                  { label: '讨论量', value: 86, tone: 'bg-primary-500' },
                  { label: '转发量', value: 72, tone: 'bg-accent-500' },
                  { label: '搜索量', value: 64, tone: 'bg-secondary-400' },
                  { label: '新素材', value: 41, tone: 'bg-primary-400' },
                ].map((bar) => (
                  <div key={bar.label}>
                    <div className="mb-1 flex items-center justify-between text-xs">
                      <span className="text-background-300">{bar.label}</span>
                      <span className="font-semibold">{bar.value}%</span>
                    </div>
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-background-800">
                      <div className={`h-full rounded-full ${bar.tone}`} style={{ width: `${bar.value}%` }} />
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-5 grid grid-cols-2 gap-3 border-t border-background-800 pt-4 text-center">
                <div>
                  <p className="font-heading text-lg font-black text-accent-400">89.2万</p>
                  <p className="mt-1 text-[11px] text-background-400">峰值热度</p>
                </div>
                <div>
                  <p className="font-heading text-lg font-black text-primary-400">+18%</p>
                  <p className="mt-1 text-[11px] text-background-400">环比变化</p>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}